import { useEffect } from 'react';
import { connect } from 'react-redux';
import { bootstrapAuth, setBootstrapped } from '../../store/actions/authActions';
import { purgeEndedBrowserSession } from '../../services/authService';
import { startSessionHeartbeat } from '../../services/sessionLifetime';

/**
 * Nothing may leave the app on its loading screen. Every route renders a
 * spinner until `bootstrapped` is true, so if this never completes the user is
 * stranded with no error and no way forward.
 */
const BOOTSTRAP_DEADLINE_MS = 12000;

/** Verifies JWT / refreshes session on every launch before reading user data. */
const AuthBootstrap = ({ bootstrapped, bootstrapAuthAction, setBootstrappedAction }) => {
  useEffect(() => {
    if (bootstrapped) return undefined;

    // Anything left by a previous run of the app goes before the first refresh
    // attempt, so a closed-and-reopened browser starts at the login screen
    // rather than silently resuming.
    purgeEndedBrowserSession();

    let settled = false;
    // Backstop: however bootstrap fails or hangs, the app becomes usable.
    const deadline = setTimeout(() => {
      if (!settled) {
        settled = true;
        setBootstrappedAction();
      }
    }, BOOTSTRAP_DEADLINE_MS);

    Promise.resolve(bootstrapAuthAction())
      .catch(() => null)
      .finally(() => {
        settled = true;
        clearTimeout(deadline);
      });

    return () => clearTimeout(deadline);
  }, [bootstrapped, bootstrapAuthAction, setBootstrappedAction]);

  // Runs for as long as the app is open, in every tab. The heartbeat is what
  // tells the next launch whether Xlog3D was still running a moment ago.
  useEffect(() => startSessionHeartbeat(), []);

  return null;
};

export default connect((state) => ({ bootstrapped: state.auth.bootstrapped }), {
  bootstrapAuthAction: bootstrapAuth,
  setBootstrappedAction: setBootstrapped,
})(AuthBootstrap);
